import React from 'react'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import { Box, Stack, Typography } from '@mui/material'
import { Modal as BootstrapModal } from 'react-bootstrap'
import ActivityMapMobileModalShell from '../../../../components/mobile/ActivityMapMobileModalShell'
import { ActivityMapMobileLoadingState } from '../../../../components/mobile'
import { MapTrifold, X } from '@phosphor-icons/react'
import ActivityReportMapLegend from './ActivityReportMapLegend'
import { resolveReportGeofence } from '../../../map/helpers/reportGeofence'
import { formatTimetoHHmm } from '../../../../helpers/utils'
import dayjs from '../../../../utils/dayjsConfig'
import ShowMapModalTimestampContent from '../../../map/ShowMapModalTimestampContent'
import ActivityReportMapDetails from './ActivityReportMapDetails'
import ReportMapSection from './ReportMapSection'
import { ACTIVITY_REPORT_MAP_COLORS, ACTIVITY_REPORT_MAP_TEXT } from './constants'

const TIMESTAMP_MAP_TEXT = {
  reportPrefix: 'Reporte:',
  defaultDetailsTitle: 'Detalles de captura de cliente:',
  defaultMobileEyebrow: 'Detalle control de horario',
  clientLabel: 'Cliente',
  dateLabel: 'Fecha',
  startTimeLabel: 'Hora inicio',
  endTimeLabel: 'Hora finalización',
  startLegendLabel: 'Inicio',
  endLegendLabel: 'Fin',
  activityLabel: 'Actividad',
  mapSectionSubtitle: 'Vista comparativa entre el inicio, el fin y la ubicación del cliente.'
}

const hasMapCoordinatePair = (latitude, longitude) =>
  [latitude, longitude].every(
    (value) => Number.isFinite(Number.parseFloat(value)) && Number.parseFloat(value) !== 0
  )

const getDateLabel = (timestamp) => {
  const dateValue =
    timestamp?.visited_date ||
    timestamp?.visit_date ||
    timestamp?.execution_date ||
    timestamp?.created_date
  const selectedDate = dateValue ? dayjs(dateValue) : null

  return selectedDate?.isValid() ? selectedDate.format('DD/MM/YYYY') : '-'
}

const getEndTimeLabel = (timestamp) => {
  if (!timestamp) return '-'

  return timestamp.status === 'completed'
    ? formatTimetoHHmm(timestamp.end_visit_time) || '-'
    : 'En curso'
}

export default function TimestampReportMapModal({
  show,
  onHide,
  onExit,
  isMobileLayout = false,
  salesmanName,
  timestamp,
  shouldRenderMobileMap,
  detailsTitle = TIMESTAMP_MAP_TEXT.defaultDetailsTitle,
  mobileEyebrow = TIMESTAMP_MAP_TEXT.defaultMobileEyebrow,
  activityName = 'Control de horario'
}) {
  const selectedDate = getDateLabel(timestamp)
  const selectedStartTime = timestamp ? formatTimetoHHmm(timestamp.start_visit_time) || '-' : '-'
  const selectedEndTime = getEndTimeLabel(timestamp)
  const hasTimestampClientMapPoint = hasMapCoordinatePair(
    timestamp?.client_latitude,
    timestamp?.client_longitude
  )
  const hasTimestampEndMapPoint = hasMapCoordinatePair(
    timestamp?.end_latitude,
    timestamp?.end_longitude
  )
  const hasTimestampStartMapPoint = hasMapCoordinatePair(
    timestamp?.start_latitude,
    timestamp?.start_longitude
  )
  const geofenceInfo = resolveReportGeofence(timestamp || {})
  const geofenceDistance = geofenceInfo.radius

  const detailItems = [
    {
      key: 'activity',
      icon: <ArticleOutlinedIcon fontSize="small" />,
      label: TIMESTAMP_MAP_TEXT.activityLabel,
      value: activityName
    },
    {
      key: 'date',
      icon: <EventOutlinedIcon fontSize="small" />,
      label: TIMESTAMP_MAP_TEXT.dateLabel,
      value: selectedDate
    },
    {
      key: 'startTime',
      icon: <ScheduleOutlinedIcon fontSize="small" />,
      label: TIMESTAMP_MAP_TEXT.startTimeLabel,
      value: selectedStartTime
    },
    {
      key: 'endTime',
      icon: <TaskAltOutlinedIcon fontSize="small" />,
      label: TIMESTAMP_MAP_TEXT.endTimeLabel,
      value: selectedEndTime
    }
  ]

  const legendItems = [
    ...(hasTimestampStartMapPoint
      ? [
          {
            key: 'start',
            label: TIMESTAMP_MAP_TEXT.startLegendLabel,
            color: 'blue',
            pinColor: '#1A73E8'
          }
        ]
      : []),
    ...(hasTimestampEndMapPoint
      ? [
          {
            key: 'end',
            label: TIMESTAMP_MAP_TEXT.endLegendLabel,
            color: 'red',
            pinColor: ACTIVITY_REPORT_MAP_COLORS.activityLegend
          }
        ]
      : []),
    ...(hasTimestampClientMapPoint
      ? [
          {
            key: 'client',
            label: TIMESTAMP_MAP_TEXT.clientLabel,
            color: 'green',
            pinColor: ACTIVITY_REPORT_MAP_COLORS.clientLegend
          }
        ]
      : []),
    {
      key: 'geofence',
      label: `${ACTIVITY_REPORT_MAP_TEXT.geofenceLegendLabel(geofenceDistance)}${
        geofenceInfo.source === 'fallback' ? ' (referencia)' : ''
      }`,
      color: 'geofence'
    }
  ]

  const renderTimestampMap = () => {
    if (!timestamp) return null

    if (isMobileLayout && !shouldRenderMobileMap) {
      return <ActivityMapMobileLoadingState />
    }

    return (
      <ShowMapModalTimestampContent
        modern
        startLongitude={timestamp.start_longitude}
        startLatitude={timestamp.start_latitude}
        endLongitude={timestamp.end_longitude}
        endLatitude={timestamp.end_latitude}
        clientLatitude={timestamp.client_latitude}
        clientLongitude={timestamp.client_longitude}
        salesmanVisitData={timestamp}
        geofenceInfo={geofenceInfo}
        recordedGeoMaxDistanceInMeter={geofenceDistance}
        isMobileView={isMobileLayout}
      />
    )
  }

  return (
    <ActivityMapMobileModalShell
      show={show && Boolean(timestamp)}
      onHide={onHide}
      onExit={onExit}
      isMobileLayout={isMobileLayout}
      ariaLabelledBy="timestamp-report-map-modal-title"
      desktopDialogClassName="modal-90w activity-report-modern"
      modern
      mobileEyebrow={mobileEyebrow}
      mobileTitle={
        <Typography className="activity-map-mobile-title">
          {`${TIMESTAMP_MAP_TEXT.reportPrefix} ${salesmanName}`}
        </Typography>
      }
      mobileBody={
        <Stack spacing={1.5} className="activity-map-mobile-layout">
          <ActivityReportMapDetails
            clientName={timestamp?.client_name}
            overline={detailsTitle}
            items={detailItems}
            isMobileLayout
          />
          <Box className="activity-map-mobile-map-card">
            <Box className="activity-map-mobile-map-card-head">
              <Box>
                <Typography className="activity-map-mobile-map-title">
                  {ACTIVITY_REPORT_MAP_TEXT.mapSectionTitle}
                </Typography>
                <Typography className="activity-map-mobile-map-subtitle">
                  {TIMESTAMP_MAP_TEXT.mapSectionSubtitle}
                </Typography>
              </Box>
              <ActivityReportMapLegend items={legendItems} isMobileLayout />
            </Box>
            <div id="map-canvas-mobile" className="activity-map-mobile-canvas">
              {renderTimestampMap()}
            </div>
          </Box>
        </Stack>
      }
      desktopChildren={
        <>
          <BootstrapModal.Header className="no-border-bottom activity-report-modern__header">
            <div className="activity-report-modern__hero-icon">
              <MapTrifold size={28} />
            </div>
            <BootstrapModal.Title id="timestamp-report-map-modal-title">
              <span className="activity-report-modern__title">{`${TIMESTAMP_MAP_TEXT.reportPrefix} ${salesmanName}`}</span>
            </BootstrapModal.Title>
            <button
              className="activity-report-modern__close"
              type="button"
              onClick={onHide}
              aria-label="Cerrar reporte de ubicación"
            >
              <X size={20} />
            </button>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <Stack spacing={2.5}>
              <ActivityReportMapDetails
                clientName={timestamp?.client_name}
                overline={detailsTitle}
                items={detailItems}
              />

              <ReportMapSection
                subtitle={TIMESTAMP_MAP_TEXT.mapSectionSubtitle}
                legendItems={legendItems}
              >
                {renderTimestampMap()}
              </ReportMapSection>
            </Stack>
          </BootstrapModal.Body>
        </>
      }
    />
  )
}
